import React, { useEffect, useMemo, useState } from 'react';
import { Grid3X3, Loader2, Search, PlayCircle, Eye, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminAPI } from '../services/api';
import GroupSubcategoriesBlock from './GroupSubcategoriesBlock';

const MatrixTable = ({ matrix, categories, query, multiOnly }) => {
  const nameOf = (slug) => categories.find(c => c.slug === slug)?.name || slug;
  const rows = useMemo(() => Object.entries(matrix)
    .filter(([sub, cats]) => (!multiOnly || cats.length > 1) && (!query || sub.toLowerCase().includes(query.toLowerCase()) || cats.some(c => nameOf(c).toLowerCase().includes(query.toLowerCase()))))
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0])), [matrix, query, multiOnly, categories]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="max-h-[420px] overflow-auto border border-gray-200 rounded-lg" data-testid="category-matrix-table">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 sticky top-0"><tr className="text-left text-xs text-gray-500 uppercase"><th className="p-2">Sous-catégorie</th><th className="p-2 w-12">Nb</th><th className="p-2">Catégories à cocher</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-gray-400 text-xs">Aucune sous-catégorie ne correspond.</td></tr>}
          {rows.map(([sub, cats]) => (
            <tr key={sub} className="border-t border-gray-100 odd:bg-white even:bg-gray-50/50" data-testid={`category-matrix-row-${sub}`}>
              <td className="p-2 font-medium text-gray-800">{sub}</td>
              <td className="p-2"><span className={`text-xs font-bold rounded-full px-2 py-0.5 ${cats.length > 1 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>{cats.length}</span></td>
              <td className="p-2"><div className="flex flex-wrap gap-1">{cats.map(c => <span key={c} className="text-[11px] bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-700">{nameOf(c)}</span>)}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MigrationBlock = ({ categories, onApplied }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const nameOf = (slug) => categories.find(c => c.slug === slug)?.name || slug;
  const run = async (dryRun) => {
    if (!dryRun && !window.confirm(`Appliquer la matrice à ${preview?.products ?? '?'} produit(s) ? Les associations existantes sont conservées, seules des catégories sont ajoutées.`)) return;
    setBusy(dryRun ? 'preview' : 'apply');
    try {
      const r = await adminAPI.applyCategoryMatrix(dryRun);
      if (dryRun) { setPreview(r); setResult(null); } else { setResult(r); setPreview(null); onApplied?.(); }
    } catch (e) { setResult({ error: e.response?.data?.detail || 'Échec de la migration' }); } finally { setBusy(''); }
  };
  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4" data-testid="category-matrix-migration">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-900">Produits existants</p>
          <p className="text-xs text-gray-600">Aperçu puis application de la matrice aux produits déjà en base. Aucune catégorie n'est retirée, la catégorie principale et les URLs ne changent pas.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => run(true)} disabled={!!busy} data-testid="category-matrix-preview-button" className="border-gray-300">
            {busy === 'preview' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />} Aperçu (dry-run)
          </Button>
          <Button onClick={() => run(false)} disabled={!!busy || !preview || preview.products === 0} data-testid="category-matrix-apply-button" className="bg-orange-500 hover:bg-orange-600">
            {busy === 'apply' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <PlayCircle size={14} className="mr-1" />} Appliquer{preview ? ` (${preview.products})` : ''}
          </Button>
        </div>
      </div>
      {preview && (
        <div className="mt-3" data-testid="category-matrix-preview">
          <p className="text-sm text-gray-800 font-medium flex items-center gap-1.5">
            {preview.products === 0 ? <CheckCircle size={15} className="text-green-600" /> : <AlertTriangle size={15} className="text-amber-600" />}
            {preview.products === 0 ? 'Tous les produits sont déjà conformes à la matrice.' : `${preview.products} produit(s) à compléter · ${preview.additions} association(s) à ajouter`}
          </p>
          {preview.sample.length > 0 && (
            <ul className="mt-2 max-h-64 overflow-auto divide-y divide-amber-100 bg-white rounded-md border border-amber-100 text-xs">
              {preview.sample.map(p => (
                <li key={p.id} className="p-2" data-testid={`category-matrix-preview-item-${p.id}`}>
                  <p className="font-medium text-gray-800 truncate">{p.name} <span className="text-gray-400 font-normal">· {p.ref} · {nameOf(p.category)}{p.subcategory ? ` › ${p.subcategory}` : ''}</span></p>
                  <p className="text-gray-600 mt-0.5">+ {p.added.map(a => `${nameOf(a.category)}${a.subcategory ? ` › ${a.subcategory}` : ''}`).join(' · ')}</p>
                </li>
              ))}
              {preview.products > preview.sample.length && <li className="p-2 text-gray-400">… et {preview.products - preview.sample.length} autre(s)</li>}
            </ul>
          )}
        </div>
      )}
      {result && (
        <p className={`mt-3 text-sm flex items-center gap-1.5 ${result.error ? 'text-red-600' : 'text-green-700'}`} data-testid="category-matrix-result">
          {result.error ? <AlertTriangle size={15} /> : <CheckCircle size={15} />} {result.error || `${result.products} produit(s) mis à jour · ${result.additions} association(s) ajoutée(s)`}
        </p>
      )}
    </div>
  );
};

// Admin view of the sub-category → categories matrix + one-off migration for existing products
const CategoryMatrixPanel = ({ categories, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [multiOnly, setMultiOnly] = useState(true);
  useEffect(() => { adminAPI.categoryMatrix().then(setData).catch(() => setData({ matrix: {}, subcategories: 0, multi: 0 })); }, []);
  return (
    <section className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-4" data-testid="category-matrix-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Grid3X3 size={18} className="text-orange-500" /> Matrice sous-catégories → catégories</h2>
          <p className="text-xs text-gray-500">Dérivée en direct de vos catégories : {data ? `${data.subcategories} sous-catégories, dont ${data.multi} présentes dans plusieurs catégories` : 'chargement…'}. Ces associations sont cochées automatiquement dans la fiche produit.</p>
        </div>
        <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700" aria-label="Fermer" data-testid="category-matrix-close"><X size={18} /></button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher une sous-catégorie ou une catégorie…" className="pl-8" data-testid="category-matrix-search" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={multiOnly} onChange={e => setMultiOnly(e.target.checked)} data-testid="category-matrix-multi-only" className="rounded border-gray-300 text-orange-500 focus:ring-orange-500" /> Uniquement les doublons</label>
      </div>
      {!data ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-orange-500" /></div> : <MatrixTable matrix={data.matrix} categories={categories} query={query} multiOnly={multiOnly} />}
      <GroupSubcategoriesBlock categories={categories} onApplied={() => { onChanged?.(); adminAPI.categoryMatrix().then(setData).catch(() => {}); }} />
      <MigrationBlock categories={categories} />
    </section>
  );
};

export default CategoryMatrixPanel;
